/**
 * Same-origin Rails API client (cookie session + CSRF).
 * Uses native fetch per system-architecture.md — not the Materio JWT $api helper.
 */

const CSRF_COOKIE = 'CSRF-TOKEN'

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`))

  return match ? decodeURIComponent(match[1]) : null
}

async function ensureCsrfToken() {
  let token = readCookie(CSRF_COOKIE)
  if (token)
    return token

  await fetch('/api/v1/csrf', {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })

  return readCookie(CSRF_COOKIE)
}

export async function railsApi(path, { method = 'GET', body, headers = {} } = {}) {
  const upperMethod = method.toUpperCase()

  const requestHeaders = {
    Accept: 'application/json',
    ...headers,
  }

  if (body !== undefined)
    requestHeaders['Content-Type'] = 'application/json'

  if (!['GET', 'HEAD', 'OPTIONS'].includes(upperMethod)) {
    const csrf = await ensureCsrfToken()
    if (csrf)
      requestHeaders['X-CSRF-Token'] = csrf
  }

  const response = await fetch(path, {
    method: upperMethod,
    credentials: 'include',
    headers: requestHeaders,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  const contentType = response.headers.get('content-type') || ''

  const payload = contentType.includes('application/json')
    ? await response.json().catch(() => ({}))
    : {}

  if (!response.ok) {
    const error = new Error(payload.error || payload.errors?.[0] || 'Request failed')

    error.status = response.status
    error.payload = payload
    throw error
  }

  return payload
}

/** Download a binary response (e.g. PDF) with the same cookie/CSRF session. */
export async function railsApiDownload(path, { filename = 'download.bin', method = 'GET', body } = {}) {
  const upperMethod = method.toUpperCase()

  const requestHeaders = {
    Accept: 'application/pdf, application/octet-stream',
  }

  if (body !== undefined)
    requestHeaders['Content-Type'] = 'application/json'

  if (!['GET', 'HEAD', 'OPTIONS'].includes(upperMethod)) {
    const csrf = await ensureCsrfToken()
    if (csrf)
      requestHeaders['X-CSRF-Token'] = csrf
  }

  const response = await fetch(path, {
    method: upperMethod,
    credentials: 'include',
    headers: requestHeaders,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!response.ok) {
    const contentType = response.headers.get('content-type') || ''

    const payload = contentType.includes('application/json')
      ? await response.json().catch(() => ({}))
      : {}

    const error = new Error(payload.error || payload.errors?.[0] || 'Download failed')

    error.status = response.status
    error.payload = payload
    throw error
  }

  const blob = await response.blob()
  const disposition = response.headers.get('content-disposition') || ''
  const match = disposition.match(/filename="?([^"]+)"?/i)
  const resolvedName = match?.[1] || filename
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')

  anchor.href = url
  anchor.download = resolvedName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}
